import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';

import { InputError } from '@common/components/input';
import Input from '@common/components/input/input';

import userInterfaceStore from '@store/user-interface';
import { TRootState } from '@store/root-reducer';
import providerApi from '@utils/api';

import ConfirmEmailSC from './confirm-email.styled';
import { initialValues, validation } from './confirm-email.schema';
import { AxiosError } from 'axios';

import StepButtons from '@components/step-buttons';
import { useIntl } from 'react-intl';

const ConfirmEmail = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | false>(false);
  const { formatMessage } = useIntl();

  // lead token
  const leadToken = useSelector(
    (store: TRootState) => store.userInterface.form.leadToken,
  );
  const email = useSelector(
    (store: TRootState) => store.userInterface.form.email,
  );

  const { values, errors, touched, handleBlur, handleChange, submitForm } =
    useFormik({
      initialValues: initialValues,
      validationSchema: validation,
      onSubmit: async (values) => {
        setLoading(true);

        try {
          const result = await providerApi.custom('/lead/verify', {
            method: 'POST',
            data: {
              code: values.code,
              leadToken,
            },
          });

          // reset error
          setApiError(false);

          dispatch(
            userInterfaceStore.updateLeadToken({
              leadToken: result.data.leadToken,
            }),
          );

          dispatch(userInterfaceStore.moveToNextStep());
        } catch (e) {
          const error = e as AxiosError['response'];
          setApiError(error?.data.data.errMessage);
        } finally {
          setLoading(false);
        }
      },
    });

  useEffect(() => {
    dispatch(
      userInterfaceStore.setNextStepDisabled({
        disabled: loading || Object.keys(errors).length > 0,
      }),
    );
  }, [loading, dispatch, errors]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setApiError(false);
    handleChange(event);
  };

  return (
    <ConfirmEmailSC>
      <div className={'header'}>
        <h4>Zadajte overovací kód</h4>
      </div>
      <div className={'content'}>
        <label>Overovací kód</label>
        <Input
          id="code"
          name="code"
          type="text"
          variation="round"
          placeholder="kód pozostávava z 6 číslic"
          value={values.code}
          onChange={handleInputChange}
          onBlur={handleBlur}
          hasError={Boolean(errors.code && touched.code) || Boolean(apiError)}
        />
        {errors.code && touched.code ? (
          <InputError error={errors.code} />
        ) : null}
        {apiError ? <InputError error={apiError} /> : null}
        <span className="description">
          Overovací kód bol odoslaný na vašu e-mailovú adresu&nbsp;
          <strong>{email}</strong>
        </span>
      </div>
      <div className={'footer'}>
        <StepButtons
          renderNextBtn={true}
          handleNextClick={submitForm}
          nextBtnLabel={formatMessage({ id: 'buttons.confirm' })}
          nextBtnLoading={loading}
        />
      </div>
    </ConfirmEmailSC>
  );
};

export default ConfirmEmail;
